import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  inject,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { GoogleAnalyticsService } from '@cropx-common/services/analytics/GoogleAnalyticsService';
import { CommonGatewayService } from '@cropx-common/services/api-gateway/CommonGatewayService';
import { EntityFilter } from '@cropx-common/services/api-gateway/entity-filter.state';
import { RefData } from '@cropx-common/services/api-gateway/refdata.state';
import {
  User,
  UserStateSelectors,
} from '@cropx-common/services/api-gateway/user.state';
import { LanguageService } from '@cropx-common/services/language/LanguageService';
import { LocalStorageService } from '@cropx-common/services/local-storage/LocalStorageService';
import { CommonApiService } from '@cropx-common/services/network/common-api.service';
import { Permissions } from '@cropx-common/services/permissions/permission.state';
import { GlobalPermissionService } from '@cropx-common/services/shared/GlobalPermissionService';
import { Subscriptions } from '@cropx-common/state/subscriptions/subscriptions.state';
import { Devices } from '@cropx-web/state/devices.state';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store, ofActionSuccessful } from '@ngxs/store';
import { combineLatest } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { CommonComponent } from '../../../../libs/common/src/lib/common.component';
import { CropRecording } from './modules/crop-recording/state/crop-recording.state';
import { ApiService } from './services/network/ApiService';
import { SharedService } from './services/shared/SharedService';
import { CropxFarms } from './state/cropx-farms.state';
import { Farms, FarmsSelectors } from './state/farms.state';
import { Fields } from './state/fields.state';
import { Product } from './state/product.state';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [CommonComponent, RouterOutlet],
})
export class AppComponent implements OnDestroy, AfterViewInit {
  private localStorageService = inject(LocalStorageService);
  private commonApiService = inject(CommonApiService);
  private apiService = inject(ApiService);
  private sharedService = inject(SharedService);
  private globalPermisionService = inject(GlobalPermissionService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private languageService = inject(LanguageService);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private commonGatewayService = inject(CommonGatewayService);
  private actions$ = inject(Actions);

  title = 'cropx-web';

  // please do not delete SharedService from constructor. The data from this service is injected in a few places.
  constructor() {
    this.loadLanguage();

    // Only login when we are not impersonating
    this.router.events
      .pipe(
        filter((val) => val instanceof NavigationEnd),
        first(),
      )
      .subscribe(() => {
        const routerState = '_routerState';
        if (this.route.snapshot[routerState].url !== '/loading') {
          this.store.dispatch(new User.Login());
        }
      });

    this.subscribeForFarm();
    this.subscribeForCropRecording();
    this.getEffluent();
    this.initializeOnLogin();

    this.onResize();
    this.getScrollbarWidth();
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  ngOnDestroy(): void {
    this.googleAnalyticsService.routerSubscription?.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.googleAnalyticsService.subscribeToPageChange(this.router);
  }

  getScrollbarWidth(): void {
    // Creating invisible container
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // forcing scrollbar to appear
    // outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    document.body.appendChild(outer);

    // Creating inner element and placing it in the container
    const inner = document.createElement('div');
    outer.appendChild(inner);

    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

    // Removing temporary elements from the DOMx
    outer.parentNode.removeChild(outer);
    document.documentElement.style.setProperty(
      '--scrollWidth',
      `${scrollbarWidth}px`,
    );
  }

  private loadLanguage(): void {
    const localeId =
      Number(this.localStorageService.getUserProps()?.localeId) || 1;

    const localeCode = LanguageService.getSelectedLanguageById(localeId).code;

    this.translateService.use(localeCode);

    // Prime ng translations
    // Make sure that the translations are included in the translations files
    // this.translateService.get('primeng').subscribe((res) => {
    //   this.config.setTranslation(res);
    // });
  }

  private initializeOnLogin(): void {
    this.actions$.pipe(ofActionSuccessful(User.Login)).subscribe(() => {
      // Quick hack to make sure user settings are refetched. This should evetually live within ngxs.
      this.getUserSettings();

      const username = this.localStorageService.getUserProps()?.username;
      this.store.dispatch(new RefData.FetchAll());

      this.store.dispatch(new User.IsDealer());
      this.store.dispatch(new User.GetCompanies());
      this.store.dispatch(new User.GetFeatureFlagsFromControl());
      this.store.dispatch(new User.GetFeaturesFromControl());
      this.store.dispatch(new Permissions.GetPermissions(username));

      this.store.dispatch(new Product.GetFertilizers());
      this.store.dispatch(new CropxFarms.FetchFarms());
      this.store.dispatch(new Farms.FetchFarms());

      this.store.dispatch(new Subscriptions.SetUsernameUUID(username));

      this.store.dispatch(new Subscriptions.GetUserCompanies());

      this.store.dispatch(new Devices.FetchDeviceInstallations());
      this.store.dispatch(new Fields.FetchFieldsDetails());
      this.store.dispatch(new EntityFilter.FetchFieldIdsAndNames());
      this.store.dispatch(new Fields.FetchLeadingIndicators());
    });
  }

  private getUserSettings() {
    const userId = this.localStorageService.getUserProps()?.userId;

    if (userId) {
      this.store.dispatch(new User.GetUserDefaults(userId));
    }
  }

  private subscribeForCropRecording(): void {
    combineLatest([
      this.store.select(UserStateSelectors.features.CropRecordingWl),
      this.store.select(FarmsSelectors.slices.activeFarmUuid),
    ])
      .pipe(filter(([wl, uuid]) => wl && uuid !== ''))
      .subscribe(([_, uuid]) => {
        this.store.dispatch(new CropRecording.FetchMeta(uuid));
      });
  }

  private subscribeForFarm(): void {
    this.store
      .select(FarmsSelectors.slices.activeFarmUuid)
      .pipe(filter((uuid) => !!uuid))
      .subscribe((uuid) => {
        if (uuid) {
          this.store.dispatch(new Farms.FetchFields(uuid));
          this.store.dispatch(new Farms.FetchCropZones(uuid));
        }
      });
  }

  // TODO: should be part of the global store.
  private getEffluent(): void {
    this.apiService.effluentApi
      .getEffluentSystemsObservable()
      .subscribe((response) => {
        if (response.content.data?.length > 0) {
          localStorage.setItem('hasEffluentSystems', JSON.stringify(true));
          this.store.dispatch(new User.HasEffluentSystem(true));
        }
      });
  }
}
